import { PlayerAdapter } from '../../internal/PlayerAdapter';
import {
  PlaybackStatistics,
  DrmScheme,
  MediaType,
  TrackInfo,
  RequestType,
  SeekRangeInfo,
  TrackType,
  TrackKind,
  PlaybackOptions,
  TrackVariantInfo,
  ThumbnailPreviewMetadata,
} from '@quickplay/player-interface';
import { createEventEmitter, EventEmitter, Error, Logger, AnyFunc } from '@quickplay/foundation';
import {
  WebMafResponse,
  LoadResponse,
  CommandStatus,
  PlayerStatusChangeResponse,
  PlayerErrorResponse,
  PlayerStreamingErrorResponse,
  PlayerSeekWindowResponse,
  VideoMetrics,
  AudioTracksResponse,
  SubtitleTracksResponse,
  WebMafTrackInfo,
  PlayerMessageResponse,
  PlayerErrorHexCode,
  PlayerTimedEventResponse,
  TimedEventType,
} from './CommandResponses';
import { Command } from './Commands';
import { convertToPlayerError, convertToPlayerStreamingError } from './ErrorHandler';
import { covertToEmsgInfo } from './Utils';

enum VideoType {
  Unknown = 0,
  MP4 = 1,
  SMOOTH_STREAMING = 2,
  DASH = 4,
  HLS = 8,
}

const VIDEO_METRICS_UPDATE_INTERVAL_MS = 500;
const AUTH_TOKEN_TYPE = 'authToken';
const AUTH_TOKEN_TYPE_VALUE = 'query';

export class WebMAFAdapter implements PlayerAdapter {
  private emitter_: EventEmitter;
  private liveSeekStartOffset_: number;

  constructor(
    private global_: any,
    private logger_: Logger,
    private contentUrl_: string,
    private playbackOptions_: PlaybackOptions,
    private licenseUrl_?: string,
    private licenseCustomData_?: string,
    private drmScheme_?: DrmScheme,
    private mediaType_?: MediaType,
  ) {
    this.emitter_ = createEventEmitter();

    this.logger_.debug(`Drm Scheme: ${this.drmScheme_} and Media type: ${this.mediaType_}`);

    this.liveSeekStartOffset_ = this.playbackOptions_.liveSeekStartOffset || 2;

    this.setupAccessFunctionHandler();
    this.setLoadConfigurations_();
  }

  private playbackRate_ = 0;
  private isBuffering_ = false;
  private isSeeking_ = false;
  private nativeCallbacks_: { [key: string]: AnyFunc[] } = {};
  private initialPlaybackTime_?: number;
  private minBitrate_ = 0;
  private maxBitrate_ = 0;
  private metricsUpdateInterval_: any = undefined;
  private allTracks_: TrackInfo[] = [];
  private hasNotifiedClosedCaptionTrack_ = false;
  private captionTrackType_?: string = undefined;
  private activeAudioTrack_?: string = undefined;
  private activeSubtitleTrack_?: string = undefined;
  private activeCaptionTrack_?: string = undefined;
  private seekableRange_?: SeekRangeInfo = undefined;
  private elapsed_ = 0;
  private duration_ = 0;
  private volume_ = 1;

  private requestInterceptor_: ((requestType: RequestType, request: any) => void) | undefined;

  public get internalPlayerName(): string {
    return 'webmaf-player';
  }

  public get internalPlayerVersion(): string {
    return this.webMafVideoPlayer().version;
  }

  public get minBitrate(): number {
    return this.minBitrate_;
  }

  public set minBitrate(bitrate: number) {
    this.setMinBitrate(bitrate);
  }

  public get maxBitrate(): number {
    return this.maxBitrate_;
  }

  public set maxBitrate(bitrate: number) {
    this.setMaxBitrate(bitrate);
  }

  public get availableBitrates(): number[] {
    return this.videoMetrics().bitrates;
  }

  public get bufferBehind(): number {
    return 0;
  }

  public get playbackRate(): number {
    return this.playbackRate_;
  }

  public set volume(volumeLevel: number) {
    this.setVolume(volumeLevel);
  }

  public get volume(): number {
    return this.volume_;
  }

  // public set playbackRate(_: number) {
  //   this.nativeCommand(Command.SET_PLAY_SPEED, { playSpeed: rate });
  // }

  public get maxBuffer(): number {
    return 0;
  }

  public get minBuffer(): number {
    return 0;
  }

  public get clockSyncUri(): string {
    return '';
  }

  public get isLive(): boolean {
    return this.videoMetrics().live;
  }

  public get playbackOptions(): PlaybackOptions {
    return this.playbackOptions_;
  }

  public get playbackStatistics(): PlaybackStatistics {
    const metrics = this.videoMetrics();
    return Object.freeze({
      bufferRange: this.webMafVideoPlayer().bufferedRange,
      decodedFrames: 0,
      droppedFrames: metrics.droppedFrames,
      corruptedFrames: 0,
      playTime: 0,
      pauseTime: 0,
      licenseTime: 0,
      bufferingTime: 0,
      requiredBandwidth: Math.round(0),
      estimatedBandwidth: metrics.bandwidth,
      variantBitrate: metrics.currentBitrate,
      videoBitrate: metrics.currentBitrate,
      audioBitrate: 0,
      loadLatency: 0,
      width: metrics.naturalWidth,
      height: metrics.naturalHeight,
      framerate: metrics.renderedFramerate,
    });
  }

  /**
   * Returns a list of tracks that can be switched in the current
   * period. If there are multiple periods, you must seek to the period in order
   * to get variants from that period.
   *
   * If the player has not loaded content, this will return an empty list.
   *
   * @return TrackInfo[]
   *
   */
  public getAllTracks(): TrackInfo[] {
    return this.allTracks_;
  }

  /**
   * Returns a list of active tracks in the current
   * period.
   *
   * If the player has not loaded content, this will return an empty list.
   *
   * @return TrackInfo[]
   *
   * @param track
   */
  public getActiveTracks(): TrackInfo[] {
    return this.getAllTracks().filter(track => {
      return (
        (track.type === TrackType.AUDIO && track.language === this.activeAudioTrack_) ||
        (track.type === TrackType.TEXT && track.language === this.activeSubtitleTrack_)
      );
    });
  }

  public getVariantTracks(): TrackVariantInfo[] {
    return [];
  }

  public getAvailableTrackVariants(_trackType: TrackType): TrackVariantInfo[] {
    return [];
  }

  public selectTrack(track?: TrackInfo): void {
    if (!track) {
      // reset subtitle tracks
      if (this.activeSubtitleTrack_) {
        this.nativeCommand(Command.SET_SUBTITLE_TRACK, { subtitleTrack: '' }, () => {
          // fire this to signal end of metadata event, so apps can reset the caption/sub rendering
          this.resetTextTrackMetadata_(TimedEventType.TEXT);
        });
        this.activeSubtitleTrack_ = undefined;
      }

      // reset caption tracks
      if (this.activeCaptionTrack_) {
        this.nativeCommand(Command.SET_CLOSED_CAPTIONS, { enable: false }, () => {
          // fire this to signal end of metadata event, so apps can reset the caption/sub rendering
          if (this.captionTrackType_) {
            this.resetTextTrackMetadata_(this.captionTrackType_);
          }
        });
        this.activeCaptionTrack_ = undefined;
      }
      return;
    }

    if (!track.language) {
      return;
    }

    if (track.type === TrackType.AUDIO) {
      this.nativeCommand(Command.SET_AUDIO_TRACK, { audioTrack: track.language });
      this.activeAudioTrack_ = track.language;
    } else if (track.kind !== TrackKind.CAPTION) {
      this.nativeCommand(Command.SET_SUBTITLE_TRACK, { subtitleTrack: track.language });
      this.activeSubtitleTrack_ = track.language;
    } else if (track.kind === TrackKind.CAPTION) {
      this.activeCaptionTrack_ = track.language;
    }
  }

  public setRequestInterceptor(interceptor: (requestType: RequestType, request: any) => void): void {
    this.requestInterceptor_ = interceptor;
  }

  public setResponseInterceptor(_: (requestType: RequestType, response: any) => void): void {
    // no-op
  }

  public accessFunctionHandler(json: string): void {
    this.nativeCallback(json);
  }

  public load(initialPlaybackTime?: number): Promise<void> {
    this.logger_.log(
      `drm: ${this.drmScheme_}, mediaType: ${this.mediaType_}, initialPlaybackTime: ${initialPlaybackTime}`,
    );

    this.initialPlaybackTime_ = initialPlaybackTime;

    const contentUrl = this.processRequestInterceptor(RequestType.MANIFEST, this.contentUrl_);
    const licenseUrl = this.processRequestInterceptor(RequestType.LICENSE, this.licenseUrl_);

    return this.webmafLoad_(contentUrl, licenseUrl, this.licenseCustomData_, this.videoType_(this.mediaType_));
  }

  public async getThumbnail(_position: number): Promise<ThumbnailPreviewMetadata | undefined> {
    return undefined;
  }

  public play(): Promise<void> {
    this.nativeCommand(Command.PLAY);
    return Promise.resolve();
  }

  public pause(): void {
    this.nativeCommand(Command.PAUSE);
  }

  public seek(seekPosition: number): void {
    this.seekTo_(seekPosition);
  }

  public seekToLiveEdge(): void {
    const seekPosition = this.getLiveEdge();
    this.seekTo_(seekPosition);
  }

  public stop(): void {
    this.nativeCommand(Command.STOP);
  }

  public unload(): Promise<void> {
    this.nativeCommand(Command.STOP);
    this.resetState_();
    return Promise.resolve();
  }

  public getLiveEdge(): number {
    if (!this.isLive) {
      return 0;
    }

    const seekableRange = this.seekableRange();
    return seekableRange ? seekableRange.end : 0;
  }

  seekableRange(): SeekRangeInfo {
    if (!this.isLive) {
      let duration = this.duration_;
      if (duration <= 0) {
        duration = Math.floor(this.videoMetrics().duration / 1000);
      }
      return { start: 0, end: duration };
    }

    return this.seekableRange_ ? this.seekableRange_ : { start: 0, end: 0 };
  }

  /**
   * @method currentOffsetFromLiveEdge returns a offset between current play head and liveEdge.
   */
  currentOffsetFromLiveEdge(): number {
    if (!this.isLive) {
      return 0;
    }

    const liveEdge = this.getLiveEdge();
    if (liveEdge > 0) {
      // calculate how far off the current play head is from the live-edge
      return liveEdge - Math.floor(this.videoMetrics().elapsed / 1000);
    }

    return 0;
  }

  public subscribe(eventName: string, callback: AnyFunc): void {
    this.emitter_.subscribe(eventName, callback);
  }

  public unsubscribe(eventName: string, callback: AnyFunc): void {
    this.emitter_.unsubscribe(eventName, callback);
  }

  public setPlaybackOptions(playbackOptions: PlaybackOptions): void {
    this.playbackOptions_ = playbackOptions;
  }

  public restrictQuality(_enableRestriction: boolean): void {
    //no-op
  }

  availableMimeTypes(_trackType: TrackType): Array<string> {
    return [];
  }

  unloadAndSavePreload(): Promise<void> {
    // no-op
    return Promise.resolve();
  }

  setPreferredMimeTypes(_trackType: TrackType, _mimeTypes: Array<string>): void {
    // no-op
  }

  restoreFromPreload(): void {
    // no-op
  }

  private videoType_(mediaFormat?: MediaType): VideoType {
    if (!mediaFormat) {
      return VideoType.Unknown;
    }

    switch (mediaFormat) {
      case MediaType.DASH:
        return VideoType.DASH;
      case MediaType.HLS:
        return VideoType.HLS;
      case MediaType.MP4:
        return VideoType.MP4;
      case MediaType.SMOOTH_STREAMING:
        return VideoType.SMOOTH_STREAMING;
      case MediaType.OTHER:
        return VideoType.Unknown;
    }
  }

  private webMafVideoPlayer(): any {
    return this.global_.WM_videoPlayer;
  }

  private processRequestInterceptor<T>(type: RequestType, url: T): T {
    if (!this.requestInterceptor_ || !url) {
      return url;
    }

    let updatedUrl = url;
    const request = { uris: [url], [AUTH_TOKEN_TYPE]: AUTH_TOKEN_TYPE_VALUE };
    this.requestInterceptor_(type, request);
    if (request.uris.length > 0) {
      updatedUrl = request.uris[0];
    }

    return updatedUrl;
  }

  private resetState_(): void {
    this.isBuffering_ = false;
    this.isSeeking_ = false;
    this.minBitrate_ = 0;
    this.maxBitrate_ = 0;
    this.allTracks_ = [];
    this.nativeCallbacks_ = {};
    this.activeAudioTrack_ = undefined;
    this.activeSubtitleTrack_ = undefined;
    this.clearProgressUpdateInterval();
    this.removeNativeEventListener(Command.PLAYER_STATUS_CHANGE, this.onStateChange_);
    this.removeNativeEventListener(Command.PLAYER_ERROR, this.onError_);
    this.removeNativeEventListener(Command.PLAYER_STREAMING_ERROR, this.onStreamingError_);
    this.removeNativeEventListener(Command.PLAYER_MESSAGE, this.onPlayerMessage_);
    this.removeNativeEventListener(Command.PLAYER_TIMED_EVENT, this.onPlayerTimedEvent_);
  }

  private setLoadConfigurations_(): void {
    const playbackOptions = this.playbackOptions_;
    this.initialPlaybackTime_ = playbackOptions.initialPlaybackTime;

    if (playbackOptions.preferredBitrate) {
      this.nativeCommand(Command.SET_VIDEO_STARTING_BANDWIDTH, { bandwidth: playbackOptions.preferredBitrate });
    }
  }

  private nativeCommand = (command: Command, args?: { [key: string]: any }, callback?: AnyFunc): void => {
    let callbacks;
    if (!(callbacks = this.nativeCallbacks_[command])) {
      callbacks = this.nativeCallbacks_[command] = [];
    }
    if (callback) {
      callbacks.push(callback);
    }

    const commandJson: { [key: string]: any } = { command: command };
    if (args) {
      for (const prop in args) {
        if (args.hasOwnProperty(prop)) {
          commandJson[prop] = args[prop];
        }
      }
    }

    const json = JSON.stringify(commandJson);
    if (this.global_.external && this.global_.external.user) {
      this.logger_.log(`WebMAF command: ${command} sent`, json);
      this.global_.external.user(json);
    }
  };

  private videoMetrics = (): VideoMetrics => {
    return this.global_.videometrics;
  };

  private setupProgressUpdateInterval = (): void => {
    if (!this.metricsUpdateInterval_) {
      this.metricsUpdateInterval_ = setInterval(() => this.updateMetricsFunc(), VIDEO_METRICS_UPDATE_INTERVAL_MS);
    }
  };

  private clearProgressUpdateInterval = (): void => {
    if (this.metricsUpdateInterval_) {
      clearInterval(this.metricsUpdateInterval_);
      this.metricsUpdateInterval_ = undefined;
    }
  };

  private setupAccessFunctionHandler = (): void => {
    this.addNativeEventListener(Command.PLAYER_STATUS_CHANGE, this.onStateChange_);
    this.addNativeEventListener(Command.PLAYER_ERROR, this.onError_);
    this.addNativeEventListener(Command.PLAYER_STREAMING_ERROR, this.onStreamingError_);
    this.addNativeEventListener(Command.PLAYER_MESSAGE, this.onPlayerMessage_);
    this.addNativeEventListener(Command.PLAYER_TIMED_EVENT, this.onPlayerTimedEvent_);

    this.global_.videometrics.onBitrateChange = this.onAdaptation_;
  };

  private updateMetricsFunc = (): void => {
    const videometrics = this.global_.videometrics;
    const elapsed = Math.floor(videometrics.elapsed / 1000);
    const duration = Math.floor(videometrics.duration / 1000);

    // note: do not fire if current value is same as last recorded value
    if (!(this.duration_ === duration && this.elapsed_ === elapsed)) {
      this.onTimeUpdate_(elapsed, duration);
      this.duration_ = duration;
      this.elapsed_ = elapsed;
    }

    videometrics.poll();

    if (this.isLive) {
      this.nativeCommand(Command.GET_SEEKWINDOW, undefined, this.onSeekWindowLoaded_);
    }
  };

  private addNativeEventListener = (name: Command, callback: any): void => {
    let callbacks;
    if (!(callbacks = this.nativeCallbacks_[name])) {
      callbacks = this.nativeCallbacks_[name] = [];
    } else {
      this.removeNativeEventListener(name, callback);
    }
    callbacks.push(callback);
  };

  private removeNativeEventListener = (name: Command, callback: any): void => {
    const callbacks = this.nativeCallbacks_[name];
    if (callbacks) {
      for (let i = 0; i < callbacks.length; i++) {
        if (callbacks[i] === callback) {
          callbacks.splice(i, 1);
          return;
        }
      }
    }
  };

  private nativeCallback = (json: string): void => {
    let data;
    try {
      data = JSON.parse(json);
    } catch (e) {
      this.logger_.warn(`Failed to parse ${json}`, e);
      return;
    }

    const callbacks = this.nativeCallbacks_[data.command];
    if (callbacks && callbacks.length > 0) {
      switch (data.command as Command) {
        // application message
        case Command.PLAYER_STATUS_CHANGE:
        case Command.PLAYER_TIMED_EVENT:
        case Command.PLAYER_STREAMING_ERROR:
        case Command.PLAYER_ERROR:
        case Command.PLAYER_MESSAGE:

        /// framework responses
        case Command.GET_AUDIO_TRACKS:
        case Command.GET_SUBTITLE_TRACKS:
        case Command.GET_PLAY_TIME:
        case Command.GET_SEEKWINDOW:
        case Command.GET_PLAY_SPEED:
        case Command.LOAD:
          // multiple event listeners may be listening for these events
          for (const cb of callbacks) {
            cb(data);
          }
          break;
        default:
          // function return (pick the earliest callback handler)
          const callback = callbacks.shift();
          if (callback) {
            callback(data);
          }
          break;
      }
    }
  };

  private onStateChange_ = (stateChange: PlayerStatusChangeResponse): void => {
    switch (stateChange.playerState) {
      case 'ready':
        if (this.allTracks_) {
          this.allTracks_ = [];
        }

        this.setPreferredTracks_();

        this.nativeCommand(Command.SET_CLOSED_CAPTIONS, { enable: true });
        this.nativeCommand(Command.SET_TIMED_METADATA, { enable: true });
        this.nativeCommand(Command.GET_AUDIO_TRACKS, undefined, this.onAudioTrackLoaded_);
        this.nativeCommand(Command.GET_SUBTITLE_TRACKS, undefined, this.onSubtitleTrackLoaded_);
        this.nativeCommand(Command.GET_SEEKWINDOW, undefined, this.onSeekWindowLoaded_);

        // The earliest opportunity to seek the content after it has been loaded is
        // once either the buffering or the playing event has been received.
        // Please note that in some instances the *buffering* event may not occur.
        // In the case that the *playing* event is used to signal a seek,
        // the first frame of the content may be displayed. This is a limitation of the webmaf player.
        //
        // For linear streams, we would need to wait until we have seekable range to perform the initial seek
        if (!this.isLive) {
          this.seekToInitialPlaybackTime();
        }
        break;
      case 'buffering':
        this.onBuffering_();
        break;
      case 'endOfStream':
        this.onPlayerIdle_();
        break;
      case 'playing':
        this.onPlaying_();
        this.setupProgressUpdateInterval();
        break;
      case 'paused':
        this.clearProgressUpdateInterval();
        break;
    }

    if (stateChange.playerState !== 'buffering' && this.isBuffering_) {
      this.onBuffering_();
    }
  };

  private setPreferredTracks_ = (): void => {
    const playbackOptions = this.playbackOptions_;
    if (playbackOptions) {
      if (playbackOptions.preferredAudioLanguage) {
        this.nativeCommand(Command.SET_AUDIO_TRACK, { audioTrack: playbackOptions.preferredAudioLanguage });
      }

      if (playbackOptions.preferredTextLanguage) {
        this.nativeCommand(Command.SET_SUBTITLE_TRACK, { subtitleTrack: playbackOptions.preferredTextLanguage });
      }
    }
  };

  private onPlayerTimedEvent_ = (message: PlayerTimedEventResponse): void => {
    if (message.datatype === TimedEventType.EMSG) {
      this.onEmsgEvent_(message);
    } else {
      this.onTimedMetadata_(message);
    }
  };

  private onPlayerMessage_ = (message: PlayerMessageResponse): void => {
    if (this.isSeeking_ && message.msg_code_hexa === PlayerErrorHexCode.PLAYER_ERROR_WAR_JUMP_COMPLETE.toLowerCase()) {
      this.onSeeking_();
    }
  };

  private onAudioTrackLoaded_ = (audioTracks: AudioTracksResponse): void => {
    this.onTracksLoaded(audioTracks.allAudioInfo, TrackType.AUDIO);
    this.activeAudioTrack_ = audioTracks.currentAudioTrack;
    this.onTracksChanged_();
  };

  private onSubtitleTrackLoaded_ = (subtitleTracks: SubtitleTracksResponse): void => {
    this.onTracksLoaded(subtitleTracks.allSubtitleInfo, TrackType.TEXT, TrackKind.SUBTITLE);
    this.activeSubtitleTrack_ = subtitleTracks.currentSubtitleTrack;
    this.onTracksChanged_();
  };

  private seekToInitialPlaybackTime(): void {
    if (this.initialPlaybackTime_ !== undefined) {
      const seekTo = this.initialPlaybackTime_;
      this.seekTo_(seekTo);
      this.initialPlaybackTime_ = undefined;
    }
  }

  private onSeekWindowLoaded_ = (seekWindow: PlayerSeekWindowResponse): void => {
    if (seekWindow.seekable) {
      const start = Math.floor(seekWindow.startTime / 1000);
      const end = Math.floor(seekWindow.endTime / 1000);
      this.seekableRange_ = { start: start, end: end };

      if (this.isLive && this.initialPlaybackTime_ !== undefined) {
        this.seekToInitialPlaybackTime();
      }
    }
  };

  private onTracksChanged_ = (): void => {
    this.emitter_.publish('trackschanged');
  };

  private onTracksLoaded = (trackInfo: WebMafTrackInfo[], type: TrackType, kind?: TrackKind): void => {
    if (!trackInfo) {
      return;
    }

    const tracks: TrackInfo[] = trackInfo.map(info => {
      return { type: type, language: info.LANGUAGE, ...(kind && { kind: kind }) };
    });

    this.allTracks_.push(...tracks);

    this.onStreamingEvent_();
  };

  private seekTo_ = (seekPosition: number): void => {
    // Bound the seek to the current seekable window
    // For Live (Restart) streams, the seek range keeps changing and seeking to beginning of the seek window
    // fails on WebMAF. Hence, we offset the seek position when seeking to seek winow start.
    const seekRange = this.seekableRange();
    const offset = this.isLive ? this.liveSeekStartOffset_ : 0;
    const playTime = Math.min(Math.max(seekRange.start + offset, seekPosition), seekRange.end);

    this.onSeeking_();
    this.nativeCommand(Command.SET_PLAYTIME, { playTime: playTime }, (response: WebMafResponse) => {
      if (response.status === CommandStatus.CommandFail) {
        this.onSeeking_();
      }
    });
  };

  private onError_ = (errorMessage: PlayerErrorResponse): void => {
    // check if there is an active seek request, reset seek state as the failure is likely caused by seek request
    if (
      this.isSeeking_ &&
      errorMessage.error_code_hexa === PlayerErrorHexCode.PLAYER_ERROR_OPERATION_FAILED.toLowerCase()
    ) {
      this.logger_.debug(
        `Received player error ${errorMessage.error_code_hexa}, bailing since this doesn't affect playback`,
      );
      this.onSeeking_();
      return;
    }

    if (errorMessage.error_code_hexa === PlayerErrorHexCode.PLAYER_ERROR_OPERATION_FAILED.toLowerCase()) {
      this.logger_.debug(
        `Received player error ${errorMessage.error_code_hexa}, bailing since this doesn't affect playback`,
      );
      return;
    }

    const playerError: Error = convertToPlayerError(errorMessage);
    this.emitter_.publish('error', playerError);
  };

  private onStreamingError_ = (errorMessage: PlayerStreamingErrorResponse): void => {
    const playerError: Error = convertToPlayerStreamingError(errorMessage);
    this.logger_.warn('Received streaming error', playerError);
  };

  private onAdaptation_ = (): void => {
    this.emitter_.publish('bandwidthchanged');
  };

  private onStreamingEvent_ = (): void => {
    this.emitter_.publish('trackavailabilitychanged');
  };

  private onPlayerIdle_ = (): void => {
    this.emitter_.publish('playeridle');
  };

  private onPlaying_ = (): void => {
    this.emitter_.publish('playerstarted');
  };

  private onSeeking_ = (): void => {
    const elapsed = Math.floor(this.videoMetrics().elapsed / 1000);
    if (this.isSeeking_) {
      this.emitter_.publish('playerseeked', elapsed);
    }
    this.isSeeking_ = !this.isSeeking_;
  };

  private onBuffering_ = (): void => {
    const eventName = this.isBuffering_ ? 'bufferingend' : 'bufferingstart';
    this.emitter_.publish(eventName);
    this.isBuffering_ = !this.isBuffering_;
  };

  private onTimeUpdate_ = (currentTime: number, duration: number): void => {
    this.emitter_.publish('timeupdate', currentTime, duration);
  };

  private isClosedCaption = (type: string): boolean => {
    switch (type) {
      case TimedEventType.CEA_608:
      case TimedEventType.CEA_708:
        return true;
    }
    return false;
  };

  private onTimedMetadata_ = (event: PlayerTimedEventResponse): void => {
    // Since we do not have an API to fetch all caption tracks, we enable captions on load
    // and observe for caption meta-data events to notify track availability
    if (this.isClosedCaption(event.datatype) && !this.hasNotifiedClosedCaptionTrack_) {
      // notify track change when detecting cc track
      const lang = event.details && event.details.language ? event.details.language : 'und';
      this.onTracksLoaded([{ LANGUAGE: lang }], TrackType.TEXT, TrackKind.CAPTION);
      this.onTracksChanged_();
      this.hasNotifiedClosedCaptionTrack_ = true;
      this.captionTrackType_ = event.datatype;
    }

    if (this.isClosedCaption(event.datatype) && !this.activeCaptionTrack_) {
      // report metadata events only when caption track is selected
      return;
    }

    this.emitter_.publish('timedmetadata', {
      type: event.datatype,
      data: event.data,
      timestamp: event.timestamp,
      details: event.details,
    });
  };

  private resetTextTrackMetadata_ = (type: string): void => {
    this.emitter_.publish('timedmetadata', { type: type });
  };

  private onEmsgEvent_ = (event: PlayerTimedEventResponse): void => {
    const data = Object.assign({ type: 'emsg' }, covertToEmsgInfo(event));
    this.emitter_.publish('timedmetadata', data);
  };

  private webmafLoad_ = async (
    url: string,
    licenseUrl?: string,
    customData?: string,
    type?: string | number,
  ): Promise<void> => {
    return new Promise((resolve, reject) => {
      this.addNativeEventListener(Command.PLAYER_STATUS_CHANGE, (stateChange: PlayerStatusChangeResponse): void => {
        if (stateChange.playerState === 'ready') {
          resolve();
        }
      });

      this.nativeCommand(
        Command.LOAD,
        {
          contentUri: url,
          licenseUri: licenseUrl,
          customData: customData,
          sourceType: type,
        },
        (loadStatus: LoadResponse) => {
          if (loadStatus.status === CommandStatus.CommandFail) {
            reject(loadStatus.message);
          }
        },
      );
    });
  };

  private setMinBitrate = (bitrate: number): void => {
    this.minBitrate_ = bitrate;
    this.nativeCommand(Command.SET_FIX_VIDEO_REPRESENTATIONS, { minBitrate: bitrate });
  };

  private setMaxBitrate = (bitrate: number): void => {
    this.maxBitrate_ = bitrate;
    this.nativeCommand(Command.SET_FIX_VIDEO_REPRESENTATIONS, { maxBitrate: bitrate });
  };

  private setVolume = (volumeLevel: number): void => {
    this.volume_ = volumeLevel;
    this.nativeCommand(Command.SET_VOLUME, { volume: volumeLevel });
  };
}
